<template>
  <div class="pg-drag">
    <!-- 문항 탭 -->
    <div class="pg-drag__tabs">
      <button
        v-for="(_, i) in sectionData.content.questions"
        :key="i"
        type="button"
        class="pg-drag__tab"
        :class="{ 'is-active': currentIndex === i }"
        @click.prevent="selectQuestion(i)"
      >
        <span>문항 {{ i + 1 }}</span>
        <span
          v-if="sectionData.content.questions.length > 1"
          class="pg-drag__tab-del"
          title="문항 삭제"
          @click.stop="removeQuestion(i)"
        >
          <i class="material-icons-round">close</i>
        </span>
      </button>
      <button type="button" class="pg-drag__tab-add" @click.prevent="addQuestion">
        <i class="material-icons-round">add</i>
        문항 추가
      </button>
    </div>

    <template v-if="current">
      <!-- Background Image -->
      <div class="p-field">
        <label class="p-field__label">Background Image</label>
        <input
          v-model="current.backgroundImage"
          type="text"
          class="p-input"
          placeholder="이미지 URL을 입력하세요 (예: /assets/img/practice/anatomy.png)"
        />
      </div>

      <!-- Image Canvas for coordinate selection -->
      <div v-if="current.backgroundImage" class="pg-drag__section">
        <div class="pg-drag__section-label">Drop Zones Setup</div>
        <div class="pg-drag__canvas">
          <div class="pg-drag__canvas-wrapper" ref="canvasWrapper">
            <img
              :src="current.backgroundImage"
              @load="onImageLoad"
              @click="onImageClick"
              ref="backgroundImg"
              class="pg-drag__bg-image"
              alt="Background"
            />
            <!-- Drop zone overlays -->
            <div
              v-for="(zone, index) in current.dropZones"
              :key="index"
              class="pg-drag__zone"
              :style="{
                left: zone.x + 'px',
                top: zone.y + 'px',
                width: zone.width + 'px',
                height: zone.height + 'px'
              }"
              @click.stop="selectDropZone(index)"
              @mousedown.stop="startMove($event, index)"
              :class="{ 'pg-drag__zone--active': selectedZoneIndex === index }"
            >
              <span class="pg-drag__zone-label">{{ zone.label || zone.id }}</span>
              <button
                class="pg-drag__zone-delete"
                @click.stop="removeDropZone(index)"
                title="제거"
              >
                <i class="material-icons-round">close</i>
              </button>
              <!-- Resize handles -->
              <template v-if="selectedZoneIndex === index">
                <div class="pg-drag__handle pg-drag__handle--nw" @mousedown.stop="startResize($event, index, 'nw')"></div>
                <div class="pg-drag__handle pg-drag__handle--n"  @mousedown.stop="startResize($event, index, 'n')"></div>
                <div class="pg-drag__handle pg-drag__handle--ne" @mousedown.stop="startResize($event, index, 'ne')"></div>
                <div class="pg-drag__handle pg-drag__handle--e"  @mousedown.stop="startResize($event, index, 'e')"></div>
                <div class="pg-drag__handle pg-drag__handle--se" @mousedown.stop="startResize($event, index, 'se')"></div>
                <div class="pg-drag__handle pg-drag__handle--s"  @mousedown.stop="startResize($event, index, 's')"></div>
                <div class="pg-drag__handle pg-drag__handle--sw" @mousedown.stop="startResize($event, index, 'sw')"></div>
                <div class="pg-drag__handle pg-drag__handle--w"  @mousedown.stop="startResize($event, index, 'w')"></div>
              </template>
            </div>
          </div>
          <div class="pg-drag__hint">
            <i class="material-icons-round">info</i>
            이미지를 클릭하여 드롭 영역을 추가하세요. 영역을 드래그하여 이동하고, 모서리와 가장자리를 드래그하여 크기를 조절할 수 있습니다.
          </div>
        </div>
      </div>

      <!-- Drop Zone Editor -->
      <div v-if="selectedZone" class="pg-drag__section">
        <div class="pg-drag__section-label">Selected Drop Zone</div>
        <div class="pg-drag__editor">
          <div class="p-form-grid p-form-grid--2">
            <div class="p-field">
              <label class="p-field__label">Zone ID</label>
              <input
                v-model="selectedZone.id"
                class="p-input"
                placeholder="영역 ID"
              />
            </div>
            <div class="p-field">
              <label class="p-field__label">Zone Label</label>
              <input
                v-model="selectedZone.label"
                class="p-input"
                placeholder="영역 라벨"
              />
            </div>
          </div>
          <div class="pg-drag__coords">
            <div class="pg-drag__coords-label">
              <i class="material-icons-round">my_location</i>
              Coordinates &amp; Size
            </div>
            <div class="p-form-grid p-form-grid--4">
              <div class="p-field">
                <label class="p-field__label">X Position</label>
                <input v-model.number="selectedZone.x" type="number" class="p-input" />
              </div>
              <div class="p-field">
                <label class="p-field__label">Y Position</label>
                <input v-model.number="selectedZone.y" type="number" class="p-input" />
              </div>
              <div class="p-field">
                <label class="p-field__label">Width</label>
                <input v-model.number="selectedZone.width" type="number" class="p-input" />
              </div>
              <div class="p-field">
                <label class="p-field__label">Height</label>
                <input v-model.number="selectedZone.height" type="number" class="p-input" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Items to drag -->
      <div class="pg-drag__section">
        <div class="pg-drag__section-label">Drag Items</div>
        <div class="pg-drag__list">
          <div
            v-for="(item, index) in current.dragItems"
            :key="index"
            class="pg-drag__item"
          >
            <div class="pg-drag__item-head">
              <span class="pg-drag__index">{{ index + 1 }}</span>
              <div class="pg-drag__item-actions">
                <button
                  type="button"
                  class="pg-drag__icon-btn pg-drag__icon-btn--danger"
                  title="제거"
                  @click.prevent="removeDragItem(index)"
                >
                  <i class="material-icons-round">delete</i>
                </button>
              </div>
            </div>
            <div class="pg-drag__item-body">
              <div class="p-field">
                <label class="p-field__label">Item Text</label>
                <input
                  v-model="item.text"
                  class="p-input"
                  placeholder="드래그할 아이템 텍스트"
                />
              </div>
              <div class="p-field">
                <label class="p-field__label">Target Zone</label>
                <select v-model="item.targetZone" class="p-input">
                  <option value="">대상 영역 선택</option>
                  <option
                    v-for="zone in current.dropZones"
                    :key="zone.id"
                    :value="zone.id"
                  >
                    {{ zone.label || zone.id }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="pg-drag__actions">
          <ABtn kind="yellow" icon="add" @click.prevent="addDragItem">Drag Item 추가</ABtn>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { defineModel, watch, ref, computed } from 'vue';
import ABtn from '@/components/Pineapple/ABtn.vue';

const sectionData = defineModel({ type: Object, required: true });
const canvasWrapper = ref(null);
const backgroundImg = ref(null);
const selectedZoneIndex = ref(null);

// 현재 편집 중인 문제 인덱스
const currentIndex = ref(0);

// Resize state management
const isResizing = ref(false);
const resizeHandle = ref('');
const startX = ref(0);
const startY = ref(0);
const startWidth = ref(0);
const startHeight = ref(0);
const startLeft = ref(0);
const startTop = ref(0);

const emptyQuestion = () => ({ backgroundImage: '', dropZones: [], dragItems: [] });

// 데이터 구조 초기화 + 레거시(단일 문항 평면 구조 / 구 problems[]) 마이그레이션
watch(() => sectionData.value.content, (content) => {
  // 직전 problems[] 단계에서 저장된 데이터 보호
  if (!Array.isArray(content.questions) && Array.isArray(content.problems)) {
    content.questions = content.problems;
    delete content.problems;
  }
  if (!Array.isArray(content.questions)) {
    if (
      content.backgroundImage !== undefined ||
      content.dropZones !== undefined ||
      content.dragItems !== undefined
    ) {
      // 기존 단일 문항 → questions[0]로 이전
      content.questions = [{
        backgroundImage: content.backgroundImage || '',
        dropZones: content.dropZones || [],
        dragItems: content.dragItems || []
      }];
    } else {
      content.questions = [emptyQuestion()];
    }
    // 레거시 평면 필드 / 사용 안 하는 필드 제거
    delete content.backgroundImage;
    delete content.dropZones;
    delete content.dragItems;
    delete content.fillMode;
    delete content.instructions;
  }
  // 각 문항 필드 보강
  content.questions.forEach((q) => {
    if (!Array.isArray(q.dropZones)) q.dropZones = [];
    if (!Array.isArray(q.dragItems)) q.dragItems = [];
    if (q.backgroundImage === undefined) q.backgroundImage = '';
  });
  // 인덱스 범위 보정
  if (currentIndex.value > content.questions.length - 1) {
    currentIndex.value = content.questions.length - 1;
  }
}, { immediate: true, deep: true });

// 현재 문항
const current = computed(() => {
  const questions = sectionData.value.content.questions;
  if (!Array.isArray(questions) || !questions.length) return null;
  const idx = Math.min(currentIndex.value, questions.length - 1);
  return questions[idx];
});

const selectedZone = computed(() => {
  if (selectedZoneIndex.value !== null && current.value && current.value.dropZones) {
    return current.value.dropZones[selectedZoneIndex.value];
  }
  return null;
});

// ── 문항 탭 ──
const selectQuestion = (index) => {
  currentIndex.value = index;
  selectedZoneIndex.value = null;
};

const addQuestion = () => {
  sectionData.value.content.questions.push(emptyQuestion());
  currentIndex.value = sectionData.value.content.questions.length - 1;
  selectedZoneIndex.value = null;
};

const removeQuestion = (index) => {
  const questions = sectionData.value.content.questions;
  if (questions.length <= 1) return;
  questions.splice(index, 1);
  if (currentIndex.value >= questions.length) {
    currentIndex.value = questions.length - 1;
  } else if (currentIndex.value > index) {
    currentIndex.value--;
  }
  selectedZoneIndex.value = null;
};

const onImageLoad = () => {
  // 이미지 로드 완료 시 처리
};

const onImageClick = (event) => {
  const rect = event.target.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  const zones = current.value.dropZones;
  const newZone = {
    id: `zone_${Date.now()}`,
    label: `Zone ${zones.length + 1}`,
    x: Math.round(x - 50),
    y: Math.round(y - 50),
    width: 100,
    height: 100
  };

  zones.push(newZone);
  selectedZoneIndex.value = zones.length - 1;
};

// Move zone functionality
const startMove = (event, index) => {
  if (event.target.classList.contains('pg-drag__handle')) {
    return;
  }

  const zone = current.value.dropZones[index];
  const rect = canvasWrapper.value.getBoundingClientRect();
  const startMouseX = event.clientX;
  const startMouseY = event.clientY;
  const startZoneX = zone.x;
  const startZoneY = zone.y;

  const handleMove = (e) => {
    const deltaX = e.clientX - startMouseX;
    const deltaY = e.clientY - startMouseY;

    zone.x = Math.max(0, Math.min(startZoneX + deltaX, rect.width - zone.width));
    zone.y = Math.max(0, Math.min(startZoneY + deltaY, rect.height - zone.height));
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMove);
  document.addEventListener('mouseup', handleMouseUp);
};

// Resize functionality
const startResize = (event, index, handle) => {
  event.preventDefault();
  event.stopPropagation();

  const zone = current.value.dropZones[index];
  isResizing.value = true;
  resizeHandle.value = handle;
  startX.value = event.clientX;
  startY.value = event.clientY;
  startWidth.value = zone.width;
  startHeight.value = zone.height;
  startLeft.value = zone.x;
  startTop.value = zone.y;

  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
};

const handleResize = (event) => {
  if (!isResizing.value || selectedZoneIndex.value === null || !current.value) return;

  const zone = current.value.dropZones[selectedZoneIndex.value];
  const deltaX = event.clientX - startX.value;
  const deltaY = event.clientY - startY.value;

  switch (resizeHandle.value) {
    case 'nw':
      zone.width = Math.max(30, startWidth.value - deltaX);
      zone.height = Math.max(30, startHeight.value - deltaY);
      zone.x = startLeft.value + (startWidth.value - zone.width);
      zone.y = startTop.value + (startHeight.value - zone.height);
      break;
    case 'n':
      zone.height = Math.max(30, startHeight.value - deltaY);
      zone.y = startTop.value + (startHeight.value - zone.height);
      break;
    case 'ne':
      zone.width = Math.max(30, startWidth.value + deltaX);
      zone.height = Math.max(30, startHeight.value - deltaY);
      zone.y = startTop.value + (startHeight.value - zone.height);
      break;
    case 'e':
      zone.width = Math.max(30, startWidth.value + deltaX);
      break;
    case 'se':
      zone.width = Math.max(30, startWidth.value + deltaX);
      zone.height = Math.max(30, startHeight.value + deltaY);
      break;
    case 's':
      zone.height = Math.max(30, startHeight.value + deltaY);
      break;
    case 'sw':
      zone.width = Math.max(30, startWidth.value - deltaX);
      zone.height = Math.max(30, startHeight.value + deltaY);
      zone.x = startLeft.value + (startWidth.value - zone.width);
      break;
    case 'w':
      zone.width = Math.max(30, startWidth.value - deltaX);
      zone.x = startLeft.value + (startWidth.value - zone.width);
      break;
  }
};

const stopResize = () => {
  isResizing.value = false;
  resizeHandle.value = '';
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
};

const selectDropZone = (index) => {
  selectedZoneIndex.value = index;
};

const addDragItem = () => {
  current.value.dragItems.push({
    text: '',
    targetZone: ''
  });
};

const removeDragItem = (index) => {
  current.value.dragItems.splice(index, 1);
};

const removeDropZone = (index) => {
  current.value.dropZones.splice(index, 1);
  if (selectedZoneIndex.value === index) {
    selectedZoneIndex.value = null;
  } else if (selectedZoneIndex.value > index) {
    selectedZoneIndex.value--;
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-drag {
  display: flex;
  flex-direction: column;
  gap: 16px;

  // ── 문제 탭 ──
  &__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding-bottom: 12px;
    border-bottom: 1px solid $p-line;
  }

  &__tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    border: 1px solid $p-line;
    background: $p-panel;
    color: $p-ink-mid;
    font-family: $p-font-body;
    font-size: 13px;
    font-weight: 700;
    border-radius: $p-radius;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease, border-color $p-dur $p-ease;

    &:hover { background: $p-panel-alt; color: $p-ink; }

    &.is-active {
      background: $p-ink;
      color: $p-point;
      border-color: $p-ink;
    }
  }

  &__tab-del {
    display: inline-grid;
    place-items: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;

    > i { font-size: 13px; }

    &:hover { background: $p-err; color: #fff; }
  }

  &__tab-add {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 7px 12px;
    border: 1px dashed $p-line-hard;
    background: transparent;
    color: $p-ink-mid;
    font-family: $p-font-body;
    font-size: 13px;
    font-weight: 700;
    border-radius: $p-radius;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover { background: $p-point-soft; color: $p-point-deep; }
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__section-label {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    letter-spacing: 0.02em;
    padding-bottom: 6px;
    border-bottom: 1px dashed $p-line;
  }

  &__canvas {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 16px;
    text-align: center;
  }

  &__canvas-wrapper {
    position: relative;
    display: inline-block;
    max-width: 100%;
    border: 1px solid $p-line-hard;
    border-radius: $p-radius-sm;
    overflow: hidden;
  }

  &__bg-image {
    max-width: 100%;
    max-height: 500px;
    cursor: crosshair;
    display: block;
    margin: 0 auto;
  }

  &__hint {
    margin-top: 12px;
    padding: 10px 12px;
    background: $p-panel-alt;
    border-radius: $p-radius-sm;
    border-left: 3px solid $p-info;
    font-size: 12px;
    color: $p-ink-mid;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 8px;

    > i {
      color: $p-info;
      font-size: 16px;
    }
  }

  &__zone {
    position: absolute;
    border: 2px solid $p-info;
    background-color: rgba(0, 122, 255, 0.12);
    cursor: move;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    min-height: 30px;
    transition: border-color $p-dur $p-ease, background-color $p-dur $p-ease;
    border-radius: $p-radius-sm;
    user-select: none;

    &:hover {
      background-color: rgba(0, 122, 255, 0.2);
    }

    &--active {
      border-color: $p-point-deep;
      background-color: rgba(255, 201, 60, 0.25);
      box-shadow: 0 0 0 3px rgba(255, 201, 60, 0.35);
    }
  }

  &__zone-label {
    font-size: 11px;
    font-weight: 700;
    color: $p-ink;
    text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.9);
    pointer-events: none;
    max-width: 100%;
    text-align: center;
    padding: 2px;
    font-family: $p-font-mono;
  }

  &__zone-delete {
    position: absolute;
    top: -10px;
    right: -10px;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: $p-err;
    color: #fff;
    border-radius: 50%;
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: transform $p-dur $p-ease;

    > i { font-size: 12px; }

    &:hover {
      transform: scale(1.1);
    }
  }

  &__handle {
    position: absolute;
    width: 8px;
    height: 8px;
    background-color: $p-panel;
    border: 2px solid $p-point-deep;
    border-radius: 50%;
    cursor: pointer;
    z-index: 10;
    transition: transform $p-dur $p-ease, background-color $p-dur $p-ease;

    &:hover {
      background-color: $p-point;
      transform: scale(1.3);
    }

    &--nw { top: -4px; left: -4px; cursor: nw-resize; }
    &--n  { top: -4px; left: 50%; transform: translateX(-50%); cursor: n-resize; }
    &--ne { top: -4px; right: -4px; cursor: ne-resize; }
    &--e  { top: 50%; right: -4px; transform: translateY(-50%); cursor: e-resize; }
    &--se { bottom: -4px; right: -4px; cursor: se-resize; }
    &--s  { bottom: -4px; left: 50%; transform: translateX(-50%); cursor: s-resize; }
    &--sw { bottom: -4px; left: -4px; cursor: sw-resize; }
    &--w  { top: 50%; left: -4px; transform: translateY(-50%); cursor: w-resize; }
  }

  &__editor {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__coords {
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
    padding: 12px 14px;
  }

  &__coords-label {
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;

    > i {
      font-size: 14px;
      color: $p-ink-soft;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    min-width: 28px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 800;
    display: inline-grid;
    place-items: center;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &--danger {
      color: $p-err;
      &:hover { background: $p-err-soft; }
    }
  }

  &__item-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
}
</style>
